import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Logo, Icon } from './Icon';
import { Menu } from './Menu';
import { Loading, ThemeToggle } from './ui';
import { useSession } from '../session';
import { applyTheme, getTheme } from '../lib/theme';
import { planName } from '../lib/plans';

const TOOLS = [
  { href: '/download', label: 'Web downloader', icon: 'download' },
  { href: '/download?tab=cloud', label: 'Cloud downloads', icon: 'cloud' },
  { href: '/app/ai', label: 'AI Studio', icon: 'sparkles' },
  { href: '/apps', label: 'Desktop apps', icon: 'monitor' },
  { href: '/ios', label: 'iPhone app', icon: 'phone' },
  { href: '/features', label: 'Features', icon: 'star' },
];
const TOOL_PATHS = ['/download', '/app/ai', '/apps', '/ios', '/features'];

export function Brand({ size = 32 }: { size?: number }) {
  return <a class="brand" href="/" aria-label="Ayonix Studio home"><Logo size={size} /><span>Ayonix <span class="grad">Studio</span></span></a>;
}

export function SiteHeader() {
  const { path } = useLocation();
  const { me, signOut } = useSession();
  const { route } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [path]);
  const toolsActive = TOOL_PATHS.some((p) => path.startsWith(p));
  const out = async () => { await signOut(); route('/'); };
  const toggleTheme = () => applyTheme(getTheme() === 'dark' ? 'light' : 'dark');
  const links = [
    { href: '/', label: 'Home', current: path === '/' },
    { href: '/discover', label: 'Discover', current: path.startsWith('/discover') },
    { href: '/library', label: 'Library', current: path.startsWith('/library') },
  ];
  return (
    <header class={`site-header${open ? ' open' : ''}`}>
      <div class="container nav-inner">
        <Brand />
        <nav id="site-nav" class="site-nav" aria-label="Main">
          {links.map((n) => <a key={n.href} href={n.href} aria-current={n.current ? 'page' : undefined}>{n.label}</a>)}
          <Menu label="Tools" triggerClass={`nav-trigger${toolsActive ? ' active' : ''}`} align="left" trigger={<>Tools <Icon name="chevronDown" size={14} /></>}
            items={TOOLS.map((t) => ({ label: t.label, icon: t.icon, href: t.href }))} />
          <a href="/pricing" aria-current={path === '/pricing' ? 'page' : undefined}>Pricing</a>
          <div class="mobile-tools" aria-label="Tools">
            {TOOLS.map((t) => <a key={t.href} href={t.href}><Icon name={t.icon} size={18} />{t.label}</a>)}
          </div>
        </nav>
        <div class="header-actions">
          {me === undefined ? <span class="avatar-ph" aria-hidden="true" /> : me ? (
            <>
              <span class={`plan-pill${me.entitlement.plan !== 'free' ? ' paid' : ''}`} data-testid="plan-badge">{planName(me)}</span>
              <Menu label="Account menu" testId="account-menu" triggerClass="avatar" trigger={<span aria-hidden="true">{me.user.email.slice(0, 1).toUpperCase()}</span>}
                items={[
                  { label: 'Account & billing', icon: 'user', href: '/app/account' },
                  { label: 'Library', icon: 'library', href: '/library' },
                  { label: 'AI Studio', icon: 'sparkles', href: '/app/ai' },
                  me.user.role === 'admin' && { label: 'Admin', icon: 'shield', href: '/app/admin' },
                  { label: 'Switch theme', icon: 'moon', onSelect: toggleTheme },
                  { label: 'Sign out', icon: 'logout', onSelect: out },
                ]} />
            </>
          ) : (
            <a class="btn primary sm" href="/login">Sign in</a>
          )}
          <button class="icon-btn menu-toggle" type="button" aria-expanded={open} aria-controls="site-nav" aria-label="Menu" onClick={() => setOpen(!open)}><Icon name={open ? 'x' : 'menu'} size={22} /></button>
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer class="site-footer">
      <div class="container footer-grid">
        <div class="stack" style="gap:10px">
          <Brand size={28} />
          <p class="muted small" style="margin:0;max-width:340px">Discover videos, save media you’re allowed to download, and understand it with AI.</p>
          <p class="muted caption" style="margin:0;max-width:340px">Works with YouTube for discovery and watching. Not affiliated with or endorsed by YouTube or Google.</p>
        </div>
        <nav aria-label="Product">
          <h2>Product</h2>
          <ul>
            <li><a href="/features">Features</a></li>
            <li><a href="/pricing">Pricing</a></li>
            <li><a href="/download">Web downloader</a></li>
            <li><a href="/apps">Desktop apps</a></li>
            <li><a href="/ios">iPhone</a></li>
          </ul>
        </nav>
        <nav aria-label="Help">
          <h2>Help</h2>
          <ul>
            <li><a href="/faq">FAQ</a></li>
            <li><a href="mailto:support@ayonix.com">support@ayonix.com</a></li>
            <li><a href="mailto:sales@ayonix.com">Contact sales</a></li>
          </ul>
        </nav>
        <nav aria-label="Legal">
          <h2>Legal</h2>
          <ul>
            <li><a href="/privacy">Privacy</a></li>
            <li><a href="/terms">Terms &amp; refunds</a></li>
            <li><a href="/copyright">Copyright</a></li>
            <li><a href="/credits">Photo credits</a></li>
          </ul>
        </nav>
      </div>
      <div class="container row between footer-base">
        <p class="caption muted" style="margin:0">© {new Date().getFullYear()} Ayonix. Photos from Wikimedia Commons under CC licences — <a href="/credits">credits</a>.</p>
        <ThemeToggle withLabel />
      </div>
    </footer>
  );
}

/**
 * Page content area. The header and footer are rendered once by the app shell (main.tsx) so they persist
 * across navigation: lazily loaded pages no longer rebuild the nav and drop focus or open menus.
 */
export function SiteLayout({ children }: { children: ComponentChildren }) {
  return <main id="main" tabIndex={-1}>{children}</main>;
}

const FOCUSED = new Set(['/login', '/signup', '/forgot', '/reset', '/verify']);

/** Footer for every page except the focused account screens (sign in, sign up, password reset, email confirmation). */
export function ChromeFooter() {
  const { path } = useLocation();
  return FOCUSED.has(path) ? null : <SiteFooter />;
}

/** Signed-in page: redirects to sign-in when there is no session. */
export function AppShell({ title, subtitle, actions, children, head = true }: { title: string; subtitle?: ComponentChildren; actions?: ComponentChildren; children: ComponentChildren; head?: boolean }) {
  const { me } = useSession();
  const { url, route } = useLocation();
  useEffect(() => { if (me === null) route(`/login?next=${encodeURIComponent(url)}`, true); }, [me]);
  return (
    <SiteLayout>
      <div class="container app-page">
        {!me ? <Loading label={me === undefined ? 'Loading your workspace…' : 'Redirecting to sign in…'} /> : (
          <>
            {head && (
              <header class="page-title row between">
                <div class="grow">
                  <h1>{title}</h1>
                  {subtitle && <p class="muted">{subtitle}</p>}
                </div>
                {actions}
              </header>
            )}
            {children}
          </>
        )}
      </div>
    </SiteLayout>
  );
}
